import { toast } from "react-toastify";
import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Data-fetching hook for list/read APIs.
 *
 * Long-term contract:
 * - Sets data + isError; does NOT toast unless showErrorToast is true.
 * - Invalidates in-flight work on unmount/deps change (no stale toasts after navigate).
 * - Passes { signal } as 2nd arg to apiFunction when supported (see services: params, options).
 */
export const useGetApi = ({
  apiFunction,
  params = {},
  dependencies = [],
  debounceDelay = 0,
  dataKey,
  countKey,
  skip = false,
  rawData = false,
  /** Main list screens: true (default). Heavy forms: pass false. */
  showErrorToast = true,
}) => {
  const [dataList, setDataList] = useState(dataKey ? [] : null);
  const [dataCount, setDataCount] = useState(0);
  const [isLoading, setIsLoading] = useState(
    !apiFunction || skip ? false : true
  );
  const [isError, setIsError] = useState(false);

  const fetchGenerationRef = useRef(0);
  const abortControllerRef = useRef(null);
  const paramsRef = useRef(params);
  const apiFunctionRef = useRef(apiFunction);

  paramsRef.current = params;
  apiFunctionRef.current = apiFunction;

  const applyResponse = useCallback(
    (response) => {
      if (response?.success || response?.code === 200) {
        let dataArray = [];
        let count = 0;

        if (Array.isArray(response.data)) {
          dataArray = response.data;
          count = response.count || dataArray.length;
        } else if (Array.isArray(response)) {
          dataArray = response;
          count = dataArray.length;
        } else if (
          response.data &&
          typeof response.data === "object" &&
          !Array.isArray(response.data)
        ) {
          if (rawData) {
            setDataList(response.data);
            setDataCount(0);
            return;
          }

          const dataObj = response.data;

          if (dataKey && dataObj[dataKey]) {
            dataArray = dataObj[dataKey];
          } else {
            const possibleArrayKeys = Object.keys(dataObj).filter((key) =>
              Array.isArray(dataObj[key])
            );
            if (possibleArrayKeys.length > 0) {
              dataArray = dataObj[possibleArrayKeys[0]];
            }
          }

          if (countKey && dataObj[countKey] !== undefined) {
            count = dataObj[countKey];
          } else if (dataObj.count !== undefined) {
            count = dataObj.count;
          } else if (dataObj.total !== undefined) {
            count = dataObj.total;
          } else {
            count = dataArray.length;
          }
        }

        setDataList(dataArray);
        setDataCount(count);
        return;
      }

      if (response?.cancelled) {
        setIsError(false);
        return;
      }

      setIsError(true);
      if (showErrorToast) {
        toast.error(response?.message || "Failed to fetch data.");
      }
      setDataList(dataKey ? [] : null);
      setDataCount(0);
    },
    [dataKey, countKey, rawData, showErrorToast]
  );

  const fetchData = useCallback(async () => {
    const fn = apiFunctionRef.current;
    if (!fn || skip) {
      return;
    }

    const generation = ++fetchGenerationRef.current;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setIsError(false);

    try {
      const response = await fn(paramsRef.current, {
        signal: controller.signal,
      });

      if (generation !== fetchGenerationRef.current) {
        return;
      }

      applyResponse(response);
    } catch (error) {
      if (generation !== fetchGenerationRef.current) {
        return;
      }
      if (error?.code === "ERR_CANCELED") {
        setIsError(false);
        return;
      }
      setIsError(true);
      if (showErrorToast) {
        toast.error(error?.message || "Failed to fetch data.");
      }
      setDataList(dataKey ? [] : null);
      setDataCount(0);
    } finally {
      if (generation === fetchGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, [skip, applyResponse]);

  useEffect(() => {
    let timer = null;

    if (!apiFunction || skip) {
      return undefined;
    }

    const run = () => {
      fetchData();
    };

    if (params?.search !== undefined) {
      timer = setTimeout(run, debounceDelay);
    } else {
      run();
    }

    return () => {
      fetchGenerationRef.current += 1;
      abortControllerRef.current?.abort();
      if (timer) {
        clearTimeout(timer);
      }
    };
    // Intentionally omit fetchData: params is often a new object each render; deps use JSON.stringify(params).
  }, [...dependencies, JSON.stringify(params), apiFunction, skip, debounceDelay]);

  return { dataList, dataCount, isLoading, isError, refetch: fetchData };
};
